<template>
    <div class="call-history-filter-toolbar">
        <CommonFilterTool
            v-model="callFilter"
            :keep-state-on-query-params="false"
            :filter-meta="filterMetas"
            :label-formatter="filterLabelFormatter"
            @addFilter="addFilter"
            :showClearAll="isClearAllEnabled()"
            @applyFilterTemplate="applyFilterTemplate"
            :filter-templates="getFilterTemplates()"
            :selected-filter-template="getSelectedFilterTemplateName()"
            :show-add-button="true"
            :required-filters="getRequiredBreakdowns()"
        >
            <template slot="extra">
                <slot name="extra" />
            </template>
        </CommonFilterTool>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import moment from "moment";
import log2 from "../../../../../utils/log2";
import CommonFilterTool from "../../../Chatbot/pages/archive/CommonFilterTool.vue";

const log = log2("CallHistoryFilterToolbar");

export default {
    name: "CallHistoryFilterToolbar",
    components: { CommonFilterTool },
    props: {
        value: {
            type: Array,
            required: true,
        },
        statusList: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            defaultFilterTemplate: "today-calls",
            filterMetas: [
                {
                    name: "dateFilter",
                    label: "Arama Tarihi",
                    materialIcon: "event",
                    controlProps: {},
                    controlType: "date-range-picker",
                    defaultValue: {
                        timeSelect: "custom",
                        startDate: null,
                        endDate: null,
                    },
                },
                {
                    name: "batch_call_id",
                    label: "Toplu Arama ID",
                    materialIcon: "batch_prediction",
                    controlType: "text-input",
                    controlProps: {
                        multiple: false,
                    },
                    defaultValue: null,
                    hidden: true,
                },
                {
                    name: "callType",
                    label: "Arama Türü",
                    materialIcon: "call",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Arama Türü Seçin",
                    },
                    defaultValue: [],
                    options: () => [
                        { id: "phone_call", label: "Telefon Araması" },
                        { id: "web_call", label: "Web Araması" },
                    ],
                },
                {
                    name: "callDirection",
                    label: "Arama Yönü",
                    materialIcon: "swap_horiz",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Arama Yönü Seçin",
                    },
                    defaultValue: [],
                    options: () => [
                        { id: "inbound", label: "Gelen Arama" },
                        { id: "outbound", label: "Giden Arama" },
                    ],
                },
                {
                    name: "agentId",
                    label: "Sesli Ajan",
                    materialIcon: "support_agent",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: false,
                        idKey: "id",
                        labelKey: "label",
                        title: "Sesli Ajan Seçin",
                    },
                    defaultValue: null,
                    options: () => this.agents.map(agent => ({ id: agent.agent_id, label: agent.agent_name })),
                },
                {
                    name: "callStatus",
                    label: "Arama Durumu",
                    materialIcon: "info",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Arama Durumu Seçin",
                    },
                    defaultValue: [],
                    options: () => [
                        { id: "ended", label: "Ended" },
                        { id: "error", label: "Error" },
                        { id: "registered", label: "Registered" },
                        { id: "initiated", label: "Initiated" },
                        { id: "in_progress", label: "In Progress" },
                    ],
                },
                {
                    name: "disconnectionReason",
                    label: "Bağlantı Kesilme Nedeni",
                    materialIcon: "call_end",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Bağlantı Kesilme Nedeni Seçin",
                    },
                    defaultValue: [],
                    options: () => [
                        { id: "user_hangup", label: "User Hangup" },
                        { id: "agent_hangup", label: "Agent Hangup" },
                        { id: "dial_failed", label: "Dial Failed" },
                        { id: "dial_no_answer", label: "Dial No Answer" },
                    ],
                },
                {
                    name: "userSentiment",
                    label: "Kullanıcı Duygu Durumu",
                    materialIcon: "mood",
                    controlType: "custom-picker",
                    controlProps: {
                        multiple: true,
                        idKey: "id",
                        labelKey: "label",
                        title: "Kullanıcı Duygu Durumu Seçin",
                    },
                    defaultValue: [],
                    options: () => [
                        { id: "Neutral", label: "Neutral" },
                        { id: "Positive", label: "Positive" },
                        { id: "Negative", label: "Negative" },
                        { id: "Unknown", label: "Unknown" },
                    ],
                },
            ],

            callFilter: _.cloneDeep(this.value),
            selectedCustomFilterTemplate: "all-calls",
            agents: [],
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
        }),
    },
    mounted() {
        this.fetchAgents();
    },
    created() {
        if (!this.selectedCustomFilterTemplate) {
            this.applyFilterTemplate(this.defaultFilterTemplate);
        } else {
            this.applyFilterTemplate(this.selectedCustomFilterTemplate);
        }
    },
    methods: {
        async fetchAgents() {
            try {
                const { VOICE_AGENT_API } = await import("../../../../../api/voiceAgent");
                const agents = await VOICE_AGENT_API.agents.list();
                this.agents = agents || [];
            } catch (error) {
                console.error("Ajanlar çekilirken hata oluştu:", error);
            }
        },
        getRequiredBreakdowns() {
            const templates = this.getFilterTemplates();
            return templates.find((x) => x.name === this.selectedCustomFilterTemplate)?.requiredBreakdowns || [];
        },
        getFilterTemplates() {
            return [
                {
                    name: "all-calls",
                    label: "Tüm Aramalar",
                    filter: () => ({}),
                    requiredFilterNames: [],
                },
                {
                    name: "today-calls",
                    label: "Bugünkü Aramalar",
                    filter: () => ({
                        dateFilter: {
                            timeSelect: "day",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["dateFilter"],
                },
                {
                    name: "this-week-calls",
                    label: "Bu Haftaki Aramalar",
                    filter: () => ({
                        dateFilter: {
                            timeSelect: "isoWeek",
                            startDate: null,
                            endDate: null,
                        },
                    }),
                    requiredFilterNames: ["dateFilter"],
                },
                {
                    name: "phone-calls",
                    label: "Telefon Aramaları",
                    filter: () => ({
                        callType: ["phone_call"],
                    }),
                    requiredFilterNames: ["callType"],
                },
                {
                    name: "web-calls",
                    label: "Web Aramaları",
                    filter: () => ({
                        callType: ["web_call"],
                    }),
                    requiredFilterNames: ["callType"],
                },
                {
                    name: "inbound-calls",
                    label: "Gelen Aramalar",
                    filter: () => ({
                        callDirection: ["inbound"],
                    }),
                    requiredFilterNames: ["callDirection"],
                },
                {
                    name: "outbound-calls",
                    label: "Giden Aramalar",
                    filter: () => ({
                        callDirection: ["outbound"],
                    }),
                    requiredFilterNames: ["callDirection"],
                },
                {
                    name: "ended-calls",
                    label: "Tamamlanan Aramalar",
                    filter: () => ({
                        callStatus: ["ended"],
                    }),
                    requiredFilterNames: ["callStatus"],
                },
                {
                    name: "error-calls",
                    label: "Hatalı Aramalar",
                    filter: () => ({
                        callStatus: ["error"],
                    }),
                    requiredFilterNames: ["callStatus"],
                },
            ];
        },
        applyFilterTemplate(name) {
            const template = this.getFilterTemplates().find((x) => x.name === name);
            log("applyFilterTemplate", name, template);

            if (template) {
                const filter = template.filter();
                const oldFilters = this.callFilter;
                const newFilters = {
                    ...oldFilters,
                    ...filter,
                };
                let requiredFilterNames = template.requiredFilterNames || [];

                const filterMetas = this.filterMetas.filter((x) => requiredFilterNames.includes(x.name));

                let currentBreakdowns = [];
                filterMetas.forEach((meta) => {
                    const item = {
                        ...meta,
                    };
                    if (meta.name === "startDate" || meta.name === "endDate") {
                        item.filterValue = newFilters[meta.name];
                    } else {
                        if (typeof (newFilters[meta.name] === "boolean")) {
                            item.filterValue = newFilters[meta.name];
                        } else {
                            item.filterValue = newFilters[meta.name] || meta.defaultValue;
                        }
                    }
                    currentBreakdowns.push(item);
                });
                
                this.callFilter = _.cloneDeep(currentBreakdowns);
                this.selectedCustomFilterTemplate = name;
            }
        },
        getSelectedFilterTemplateName() {
            const templates = this.getFilterTemplates();
            if (this.selectedCustomFilterTemplate) {
                return templates.find((x) => x.name === this.selectedCustomFilterTemplate)?.label;
            }
            return null;
        },
        isClearAllEnabled() {
            return this.callFilter.length > 0;
        },
        addFilter(name, value, autoOpen = true) {
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.callFilter.find((x) => x.name === name);
            log.info("addFilter", { name, value, meta, filter });

            if (name === "batch_call_id" && value) {
                if (filter) {
                    this.callFilter = this.callFilter.map((x) => {
                        if (x.name === name) {
                            x.filterValue = value;
                        }
                        return x;
                    });
                } else {
                    this.callFilter.push({
                        ...meta,
                        filterValue: value,
                    });
                }
                this.$emit("input", this.callFilter);
                return;
            }
            
            if (filter) {
                this.callFilter = this.callFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.callFilter.push({
                    ...meta,
                    filterValue: value || meta.defaultValue,
                });
            }
            
            if (autoOpen) {
                setTimeout(() => {
                    const el = document.querySelector(".filter-wrapper-preview[data-filter-name=" + name + "]");
                    if (el) {
                        el.click();
                    }
                }, 100);
            }
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.callFilter = this.callFilter.filter((x) => x.name !== name);
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            
            if (!filter) {
                return name + ": " + value;
            }
            
            if (filter.controlType === "custom-picker") {
                const values = Array.isArray(value) ? value : [value];
                const selectedValues = values
                    .map((value) => {
                        const option = filter.options().find((x) => x.id == value);
                        return option?.label || value;
                    })
                    .filter(Boolean);
                return `${filter.label}: ${selectedValues.join(", ")}`;
            } else if (filter.controlType === "date-range-picker") {
                const shortcuts = {
                    today: "Bugün",
                    day: "Bugün",
                    "-day": "Dün",
                    isoWeek: "Bu Hafta",
                    "-isoWeek": "Geçen Hafta",
                    7: "Son 7 Gün",
                    30: "Son 30 Gün",
                    month: "Bu Ay",
                    "-month": "Geçen Ay",
                    yesterday: "Dün",
                    "last-7-days": "Son 7 Gün",
                    "last-30-days": "Son 30 Gün",
                    thisWeek: "Bu Hafta",
                    lastWeek: "Geçen Hafta",
                    thisMonth: "Bu Ay",
                    lastMonth: "Geçen Ay",
                    last24h: "Son 24 Saat",
                    custom: "Özel",
                };
                
                if (value.timeSelect && value.timeSelect !== "custom") {
                    return `${shortcuts[value.timeSelect] || value.timeSelect}`;
                } else {
                    const startDate = value.startDate ? moment(value.startDate).format("DD MMM YYYY") : "";
                    const endDate = value.endDate ? moment(value.endDate).format("DD MMM YYYY") : "";

                    if (startDate && endDate) {
                        if (startDate === endDate) {
                            return `${startDate}`;
                        } else {
                            return `${startDate} - ${endDate}`;
                        }
                    } else {
                        return filter.label;
                    }
                }
            }

            if (value) {
                return `${filter.label}: ${value}`;
            }
            
            return filter.label;
        },
        processFilters(options) {
            this.filters.forEach((filter) => {
                if (filter.name === "dateFilter" && filter.filterValue) {
                    if (filter.filterValue.startDate && filter.filterValue.endDate) {
                        options.filter_criteria.start_timestamp = {
                            lower_threshold: moment(filter.filterValue.startDate).valueOf(),
                            upper_threshold: moment(filter.filterValue.endDate).valueOf(),
                        };
                    }
                } else if (filter.name === "batch_call_id" && filter.filterValue) {
                    options.filter_criteria.batch_call_id = [filter.filterValue];
                } else if (filter.name === "callType" && filter.filterValue) {
                    // ... existing code ...
                }
            });
        },
    },
    watch: {
        callFilter: {
            handler: function () {
                this.$emit("input", this.callFilter);
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
.call-history-filter-toolbar {
    display: flex;
    width: 100%;

    .common-filter-tool {
        border-bottom: none;
        overflow: hidden;
        padding: 0px;

        .filters {
            overflow-x: auto;
            overflow-y: hidden;
            scrollbar-width: none;
        }

        .add-button {
            margin: unset !important;
            border-radius: 20px;
            border: 1px dashed var(--color-border-default);
            font-size: 12px;
            font-weight: 600;
            line-height: 24px;
            padding: 1px 5px !important;
            min-width: fit-content;

            .material-icons {
                font-size: 15px;
                margin-right: 3px;
            }
        }

        .clear-preview {
            font-size: 12px;
            font-weight: 600;
            line-height: 24px;

            .material-icons {
                font-size: 15px;
                margin-right: 3px;
            }
        }

        .filter-wrapper-preview {
            border-radius: 20px;
            padding: 1px 5px !important;
            background: white;
            color: black;
            border: 1px solid var(--color-border-default);

            .filter-value-text {
                font-size: 12px;
                font-weight: 600;
                color: #22272d;
                line-height: 24px;
            }

            .material-icons {
                margin-right: 3px;
            }
        }
    }
}
</style> 